import React, { useState, useContext, createContext, useMemo } from "react";
import { AdminStateContext } from "../../../../../providers/AdminStateProvider.jsx";
import styles from "./JournalStatusReport.module.css";
import { useJournalStatusReport } from "../../hooks/useJournalStatusReport";

const ReportContext = createContext();

// TODO: Move this into a CMS
const PLATFORMS = {
    cl: [
        { value: "khipu", label: "Khipu" },
        { value: "otros_pagos", label: "Otros Pagos" },
        { value: "movement_id", label: "Movement ID" },
        { value: "payroll", label: "Nomina de Pago" },
    ],
    mx: [
        { value: "payu", label: "PayU" },
        { value: "openpay", label: "Openpay" },
        { value: "fintoc", label: "Fintoc" },
        { value: "movement_id", label: "Movement ID" },
        { value: "payroll", label: "Nomina de Pago" },
    ],
    co: [
        { value: "place_to_pay", label: "Place to Pay" },
        { value: "movement_id", label: "Movement ID" },
        { value: "payroll", label: "Nomina de Pago" }
    ]
};

const JournalStatusReport = ({ children }) => {
    const [adminState] = useContext(AdminStateContext);
    const { country } = adminState;
    const { generateReport } = useJournalStatusReport();

    const [platform, setPlatform] = useState("");
    const [textInput, setTextInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");
    const [resultZipUrl, setResultZipUrl] = useState(null);
    const [resultZipName, setResultZipName] = useState("");
    const [error, setError] = useState(null);
    const [showHelp, setShowHelp] = useState(false);

    const availablePlatforms = useMemo(() => PLATFORMS[country?.toLowerCase()] || [], [country]);

    React.useEffect(() => {
        const isValid = availablePlatforms.find(p => p.value === platform);
        if (!isValid && availablePlatforms.length > 0) {
            setPlatform(availablePlatforms[0].value);
        }
    }, [country, availablePlatforms, platform]);

    const handleGenerateReport = async () => {
        if (!textInput.trim()) {
            setError("Por favor ingresa referencias.");
            return;
        }

        setLoading(true);
        setProgress(0);
        setStatusMessage("Iniciando...");
        setError(null);
        setResultZipUrl(null);

        try {
            const references = textInput.split(/[\n, ]+/).map(r => r.trim()).filter(r => r);

            if (references.length === 0) {
                throw new Error("No se encontraron referencias válidas.");
            }

            const { url, filename } = await generateReport({
                country,
                platform,
                references,
                onProgress: setProgress,
                onStatus: setStatusMessage
            });

            setResultZipUrl(url);
            setResultZipName(filename);
            setStatusMessage("¡Listo!");

        } catch (err) {
            console.error(err);
            setError(err.message);
            setStatusMessage("Error");
        } finally {
            setLoading(false);
        }
    };

    const value = {
        country,
        platforms: availablePlatforms,
        platform,
        setPlatform,
        textInput,
        setTextInput,
        generateReport: handleGenerateReport,
        loading,
        progress,
        statusMessage,
        resultZipUrl,
        resultZipName,
        error,
        showHelp,
        setShowHelp
    };

    return (
        <ReportContext.Provider value={value}>
            <div className={styles.container}>
                {children}
            </div>
        </ReportContext.Provider>
    );
};

// --- Subcomponents ---

const HelpModal = () => {
    const { showHelp, setShowHelp, country, platform } = useContext(ReportContext);

    if (!showHelp) return null;

    const imagePath = `/img/erp/references/${country?.toLowerCase()}/${platform}.png`;

    return (
        <div className={styles.modalOverlay} onClick={() => setShowHelp(false)}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                <button className={styles.closeButton} onClick={() => setShowHelp(false)}>×</button>
                <h3 style={{ marginBottom: '16px', marginTop: 0 }}>Ejemplo de Referencia: {platform}</h3>
                <img
                    src={imagePath}
                    alt={`Reference example for ${platform}`}
                    className={styles.modalImage}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                    }}
                />
                <div className={styles.noImageText} style={{ display: 'none' }}>
                    No hay imagen de ejemplo disponible para esta plataforma.
                </div>
            </div>
        </div>
    );
};

const Header = () => {
    return <h2 className={styles.headerTitle}>REPORTE ESTADO DE ASIENTOS ERP</h2>;
};

const Controls = () => {
    const { platforms, platform, setPlatform, generateReport, loading, setShowHelp } = useContext(ReportContext);

    return (
        <div className={styles.formRow}>

            <div className={styles.formGroup}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        padding: '10px 12px',
                        backgroundColor: '#f4f5f7',
                        border: '1px solid #e1e4e8',
                        borderRight: 'none',
                        borderTopLeftRadius: '6px',
                        borderBottomLeftRadius: '6px',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}>
                        Plataforma
                    </div>
                    <select
                        className={styles.select}
                        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                    >
                        {platforms.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                    <button
                        className={styles.helpButton}
                        onClick={() => setShowHelp(true)}
                        title="Ver ejemplo de referencia"
                    >
                        ❓
                    </button>
                </div>
            </div>

            <button
                className={styles.generateButton}
                onClick={generateReport}
                disabled={loading}
            >
                {loading ? "Generando..." : "Generar"}
            </button>
        </div>
    );
};

const InputArea = () => {
    const { textInput, setTextInput } = useContext(ReportContext);

    return (
        <div className={styles.inputContainer} style={{ display: 'block', minHeight: 'auto', padding: 0 }}>
            <div className={styles.label}>Referencias</div>
            <textarea
                className={styles.textArea}
                placeholder="Ingresa las referencias separadas por coma, espacio o salto de línea..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
            />
        </div>
    );
};

const ProgressSection = () => {
    const { loading, progress, statusMessage, resultZipUrl, resultZipName, error } = useContext(ReportContext);

    if (!loading && !resultZipUrl && !error) return null;

    return (
        <div className={styles.statusContainer}>
            {error ? (
                <div className={styles.error}>Error: {error}</div>
            ) : (
                <>
                    <div className={styles.statusText}>{statusMessage}</div>
                    {loading && (
                        <div className={styles.progressBarContainer}>
                            <div
                                className={styles.progressBarFill}
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    )}
                    {resultZipUrl && (
                        <div>
                            <a
                                href={resultZipUrl}
                                download={resultZipName}
                                className={styles.downloadButton}
                            >
                                ⬇ Descargar {resultZipName}
                            </a>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// Combine components
JournalStatusReport.Header = Header;
JournalStatusReport.Controls = Controls;
JournalStatusReport.InputArea = InputArea;
JournalStatusReport.ProgressSection = ProgressSection;
JournalStatusReport.HelpModal = HelpModal;

export default JournalStatusReport;
